/**
 * fabric-quantity.js
 *
 * Customer-facing fractional quantity selector for fabric.
 *
 * IMPORTANT:
 * Shopify's native cart quantity is integer-only.
 * Therefore this file keeps the customer-facing value as metres/kg, while
 * Shopify receives the equivalent integer number of selling steps.
 *
 * Example:
 *   step = 0.05
 *   customer selects 0.25 m
 *   Shopify receives 5 internal units
 *
 * The customer never sees "5 units"; the cart/order property shows "0.25 m".
 */

(function () {
  if (customElements.get('fabric-quantity')) return;

  const FALLBACK_STEP = 0.05;
  const FALLBACK_MIN = 0.25;
  const FALLBACK_MAX = 50;

  function route(name, fallback) {
    const routes = window.Theme && window.Theme.routes;
    return (routes && routes[name]) || fallback;
  }

  function fetchCart() {
    return fetch(route('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  function postChange(payload) {
    return fetch(route('cart_change_url', '/cart/change') + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    }).then((response) =>
      response.json().then((data) => {
        if (!response.ok || !data || data.status) {
          throw new Error(
            String(
              (data && (data.description || data.message)) ||
                'Could not update the cart.'
            )
          );
        }

        return data;
      })
    );
  }

  function unitsInCart(cart, variantId) {
    let total = 0;

    ((cart && cart.items) || []).forEach((item) => {
      if (String(item.variant_id) === String(variantId)) {
        total += Number(item.quantity) || 0;
      }
    });

    return total;
  }

  function reconcileQuantityText(cart, variantId, formatQuantity) {
    const stale = [];

    ((cart && cart.items) || []).forEach((item) => {
      if (String(item.variant_id) !== String(variantId)) return;

      const properties = item.properties;

      if (!properties || typeof properties !== 'object') return;
      if (properties.Quantity == null) return;

      const expected = formatQuantity(item.quantity);

      if (String(properties.Quantity) === expected) return;

      stale.push({
        id: item.key,
        quantity: item.quantity,
        properties: Object.assign({}, properties, {
          Quantity: expected,
        }),
      });
    });

    if (!stale.length) {
      return Promise.resolve({
        cart,
        changed: false,
      });
    }

    let chain = Promise.resolve(null);

    stale.forEach((item) => {
      chain = chain.then(() => postChange(item));
    });

    return chain.then((updatedCart) => ({
      cart:
        updatedCart && updatedCart.items
          ? updatedCart
          : cart,
      changed: true,
    }));
  }

  function stockErrorMessage(response, cart, variantId, formatQuantity) {
    const raw = String(
      (response && (response.description || response.message)) ||
        'Could not add this item to your cart.'
    );

    const looksLikeStockError =
      /sold out|out of stock|in stock|availab|maximum|can.t add more|cannot add more|can only add|only \d+/i.test(
        raw
      );

    if (!looksLikeStockError) return raw;

    const availableUnits = unitsInCart(cart, variantId);

    if (availableUnits > 0) {
      return (
        'Only ' +
        formatQuantity(availableUnits) +
        ' is available, and the available quantity is now in your cart.'
      );
    }

    return 'Sorry, this fabric is out of stock.';
  }

  function hasExpectedLine(cart, variantId, expectedUnits, formatQuantity) {
    if (!(expectedUnits > 0)) return false;
    return ((cart && cart.items) || []).some((item) =>
      String(item.variant_id) === String(variantId) &&
      Number(item.quantity) === expectedUnits &&
      item.properties && String(item.properties.Quantity) === formatQuantity(expectedUnits)
    );
  }

  function afterAddToCart(addResponse, variantId, formatQuantity, expectedUnits) {
    const failed = !!(addResponse && addResponse.status);

    return fetchCart()
      .then((cart) =>
        reconcileQuantityText(cart, variantId, formatQuantity)
          .then((result) => ({
            ok: !failed && hasExpectedLine(result.cart, variantId, expectedUnits, formatQuantity),
            message: failed
              ? stockErrorMessage(addResponse, result.cart, variantId, formatQuantity)
              : hasExpectedLine(result.cart, variantId, expectedUnits, formatQuantity)
                ? ''
                : 'The cart quantity did not match your selection. Please review your cart before checkout.',
            cart: result.cart,
            changed: result.changed,
          }))
      )
      .catch(() => ({
        ok: false,
        message: failed
          ? stockErrorMessage(addResponse, null, variantId, formatQuantity)
          : 'Could not verify the cart quantity. Please try again.',
        cart: null,
        changed: false,
      }));
  }

  function announceCartChange(cart, variantId) {
    document.dispatchEvent(new CustomEvent('tessuti:cart-change'));

    if (!cart) return;

    if (
      window.Shopify &&
      window.Shopify.designMode
    ) {
      return;
    }

    import('@shopify/events')
      .then((mod) => {
        document.dispatchEvent(
          new mod.CartLinesUpdateEvent({
            action: 'update',
            context: 'product',
            lines: [
              {
                merchandiseId: String(variantId),
                quantity: unitsInCart(cart, variantId),
              },
            ],
            promise: Promise.resolve({
              cart: mod.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                items: cart.items,
                source: 'fabric-quantity',
                itemCount: cart.item_count,
                didError: false,
              },
            }),
          })
        );
      })
      .catch(() => {});
  }

  class FabricQuantity extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const data = this.dataset;

      this.uom = data.uom || '';

      this.step = this.toNumber(
        data.step,
        FALLBACK_STEP
      );

      this.min = this.toNumber(
        data.min,
        FALLBACK_MIN
      );

      this.max = this.toNumber(
        data.max,
        FALLBACK_MAX
      );

      this.divisor = Math.max(
        1,
        Math.round(
          this.toNumber(
            data.divisor,
            1 / this.step
          )
        )
      );

      this.unitPrice = Math.round(
        this.toNumber(data.unitPrice, 0)
      );

      this.currency =
        data.currency ||
        document.documentElement.dataset.currency ||
        'INR';

      /*
       * Defensive fallback.
       * This component is for fractional fabric quantities.
       */
      if (!(this.step > 0) || this.step >= 1) {
        this.step = FALLBACK_STEP;
        this.divisor = Math.round(1 / this.step);
      }

      if (!(this.min > 0)) {
        this.min = FALLBACK_MIN;
      }

      if (!(this.max > 0)) {
        this.max = FALLBACK_MAX;
      }

      this.decimals = this.getDecimalPlaces(this.step);

      /*
       * Internal integer units.
       *
       * Example:
       * step = 0.05
       * 0.25 m = 5 internal units
       *
       * This conversion is only for Shopify's native integer quantity.
       * It is never shown to the customer.
       */
      this.minUnits = Math.max(
        1,
        Math.round(this.min / this.step)
      );

      this.maxUnits = Math.max(
        this.minUnits,
        Math.round(this.max / this.step)
      );

      /*
       * Keep the buttons simple:
       * + / - changes by the same step that the product uses.
       *
       * Example:
       * 0.25 -> 0.30 -> 0.35
       */
      this.buttonUnits = 1;

      this.display = this.querySelector(
        '[data-fq-display]'
      );

      this.unitsInput = this.querySelector(
        '[data-fq-units]'
      );

      this.propertyInput = this.querySelector(
        '[data-fq-prop]'
      );

      this.totalElement = this.querySelector(
        '[data-fq-total]'
      );

      this.errorElement = this.querySelector(
        '[data-fq-error]'
      );

      const minus = this.querySelector(
        '[data-fq-minus]'
      );

      const plus = this.querySelector(
        '[data-fq-plus]'
      );

      if (minus) {
        minus.addEventListener('click', (event) => {
          event.preventDefault();

          this.setUnits(
            this.units - this.buttonUnits
          );
        });
      }

      if (plus) {
        plus.addEventListener('click', (event) => {
          event.preventDefault();

          this.setUnits(
            this.units + this.buttonUnits
          );
        });
      }

      if (this.display) {
        this.display.addEventListener('input', () => {
          this.previewTyped();
        });

        this.display.addEventListener('blur', () => {
          this.commitTyped();
        });

        this.display.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.display.blur();
          }
        });
      }

      /*
       * Start from the Liquid minimum.
       */
      const initialQuantity = this.parseQuantity(
        this.display ? this.display.value : ''
      );

      this.units = Number.isFinite(initialQuantity)
        ? this.quantityToUnits(initialQuantity)
        : this.minUnits;

      this.setUnits(this.units, {
        silent: true,
      });
    }

    toNumber(value, fallback) {
      const number = parseFloat(value);

      return Number.isFinite(number)
        ? number
        : fallback;
    }

    getDecimalPlaces(value) {
      const text = String(value);

      if (!text.includes('.')) return 0;

      return text.split('.')[1].replace(/0+$/, '').length;
    }

    parseQuantity(value) {
      return parseFloat(
        String(value == null ? '' : value)
          .replace(',', '.')
          .trim()
      );
    }

    quantityToUnits(quantity) {
      if (!Number.isFinite(quantity)) {
        return this.minUnits;
      }

      /*
       * Convert the customer quantity into Shopify's internal integer units.
       * 0.25 / 0.05 = 5
       */
      return Math.round(
        quantity / this.step
      );
    }

    unitsToQuantity(units) {
      return units * this.step;
    }

    formatQuantityFromUnits(units) {
      return this.unitsToQuantity(units).toFixed(
        this.decimals
      );
    }

    formatQuantity(units) {
      const quantity = this.unitsToQuantity(units);

      let text = quantity.toFixed(
        Math.max(this.decimals, 2)
      );

      text = text.replace(/\.?0+$/, '');

      return text;
    }

    clampUnits(units) {
      units = Math.round(units);

      if (units < this.minUnits) {
        return this.minUnits;
      }

      if (units > this.maxUnits) {
        return this.maxUnits;
      }

      return units;
    }

    paint(units, updateDisplay) {
      const quantityText =
        this.formatQuantityFromUnits(units);

      const quantity =
        this.unitsToQuantity(units);

      /*
       * Customer sees the real quantity:
       * 0.25 m
       *
       * NOT:
       * 5 units
       */
      if (updateDisplay && this.display) {
        this.display.value = quantityText;
      }

      /*
       * Shopify receives its required integer.
       */
      if (this.unitsInput) {
        this.unitsInput.value = String(units);

        this.unitsInput.dispatchEvent(
          new Event('input', {
            bubbles: true,
          })
        );

        this.unitsInput.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      }

      /*
       * Customer-visible line item property.
       */
      if (this.propertyInput) {
        this.propertyInput.value =
          this.formatQuantity(units) +
          (this.uom ? ' ' + this.uom : '');
      }

      /*
       * Total price.
       *
       * variant.price is the price of ONE Shopify step.
       * Example:
       * ₹10 / 0.05 m
       * 5 internal units = ₹50
       */
      if (this.totalElement && this.unitPrice > 0) {
        const totalMinor =
          this.unitPrice * units;

        this.totalElement.textContent =
          this.money(totalMinor);
      }

      return quantity;
    }

    previewTyped() {
      if (!this.display) return;

      const quantity = this.parseQuantity(
        this.display.value
      );

      if (!Number.isFinite(quantity)) return;

      const units =
        this.quantityToUnits(quantity);

      this.paint(
        this.clampUnits(units),
        false
      );
    }

    commitTyped() {
      if (!this.display) return;

      const quantity = this.parseQuantity(
        this.display.value
      );

      if (!Number.isFinite(quantity)) {
        this.setUnits(this.units);
        return;
      }

      const units =
        this.quantityToUnits(quantity);

      const clamped =
        this.clampUnits(units);

      const snappedQuantity =
        this.unitsToQuantity(clamped);

      const wasAdjusted =
        Math.abs(snappedQuantity - quantity) >
        0.000001;

      this.setUnits(clamped, {
        adjusted: wasAdjusted,
      });
    }

    setUnits(units, options) {
      options = options || {};

      units = this.clampUnits(units);

      this.units = units;

      const quantity =
        this.paint(units, true);

      if (this.errorElement) {
        let message = '';

        if (
          options.adjusted &&
          quantity < this.min
        ) {
          message =
            'Minimum ' +
            this.formatQuantity(this.minUnits) +
            (this.uom ? ' ' + this.uom : '') +
            ' per order.';
        } else if (
          options.adjusted &&
          quantity > this.max
        ) {
          message =
            'Maximum ' +
            this.formatQuantity(this.maxUnits) +
            (this.uom ? ' ' + this.uom : '') +
            ' per order.';
        } else if (options.adjusted) {
          message =
            'Quantity adjusted to the nearest ' +
            this.formatQuantityFromUnits(units) +
            (this.uom ? ' ' + this.uom : '') +
            '.';
        }

        this.errorElement.textContent = message;

        if (message) {
          this.errorElement.removeAttribute(
            'hidden'
          );
        } else {
          this.errorElement.setAttribute(
            'hidden',
            ''
          );
        }
      }

      if (!options.silent) {
        this.dispatchEvent(
          new CustomEvent(
            'fabric-quantity:change',
            {
              bubbles: true,
              detail: {
                units,
                quantity,
                uom: this.uom,
              },
            }
          )
        );
      }
    }

    /*
     * Called by the product section if the variant changes.
     */
    setUnitPrice(price) {
      const newPrice = Math.round(
        this.toNumber(price, 0)
      );

      if (!newPrice) return;

      this.unitPrice = newPrice;

      const priceElement =
        this.querySelector(
          '.fabric-quantity__price'
        );

      if (priceElement) {
        priceElement.textContent =
          this.money(
            newPrice * this.divisor
          ) +
          (this.uom ? '/' + this.uom : '');
      }

      this.paint(this.units, false);
    }

    quantityText(units) {
      return (
        this.formatQuantity(units) +
        (this.uom ? ' ' + this.uom : '')
      );
    }

    /*
     * Compatibility methods for the existing product
     * section / cart integration.
     */
    afterAdd(addResponse, sectionIds, expectedUnits) {
      const form = this.closest('form');

      const idInput =
        form &&
        form.querySelector('[name="id"]');

      const variantId =
        idInput && idInput.value;

      return afterAddToCart(
        addResponse,
        variantId,
        (units) => this.quantityText(units),
        expectedUnits
      );
    }

    variantUnitsInCart(cart, variantId) {
      return unitsInCart(cart, variantId);
    }

    showCartMessage(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message || '';
      if (message) this.errorElement.removeAttribute('hidden');
      else this.errorElement.setAttribute('hidden', '');
    }

    announceCart(cart, variantId) {
      announceCartChange(
        cart,
        variantId
      );
    }

    money(minorUnits) {
      const amount =
        Number(minorUnits) / 100;

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang ||
            'en-IN',
          {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 2,
          }
        ).format(amount);
      } catch (error) {
        return amount.toFixed(2);
      }
    }
  }

  customElements.define(
    'fabric-quantity',
    FabricQuantity
  );
})();
